// src/components/DigitalCard/DigitalCardFilter.js
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';

/**
 * A component that filters content based on the user's Digital Card preferences
 * @param {Object} props - Component properties
 * @param {Array} props.content - Array of content items to filter
 * @param {Function} props.onFilteredContent - Callback function for filtered content
 * @param {string} props.filterMode - Filter mode: 'curated', 'global', or 'personal-only'
 */
const DigitalCardFilter = ({ content, onFilteredContent, filterMode = 'curated' }) => {
  const { user, savedItems } = useContext(UserContext);
  const [filteredContent, setFilteredContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Skip filtering if no content or user preferences
    if (!content || content.length === 0) {
      setFilteredContent([]);
      setIsLoading(false);
      return;
    }

    const applyFilter = async () => {
      setIsLoading(true);
      
      // If filter mode is global, just return all content
      if (filterMode === 'global' || !user?.curationSignature) {
        console.log('Global mode or no curation signature, showing all content');
        setFilteredContent(content);
        setIsLoading(false);
        return;
      }

      // If filter mode is personal-only, just return liked items
      if (filterMode === 'personal-only') {
        const likedItemIds = savedItems || user?.history?.likedItemIds || [];
        console.log('Personal-only mode, showing', likedItemIds.length, 'liked items');
        const personalContent = content.filter(item => 
          likedItemIds.includes(item.id || item.originalId)
        );
        setFilteredContent(personalContent);
        setIsLoading(false);
        return;
      }

      // If filter mode is curated, apply the card filter
      try {
        console.log('Curated mode, scoring', content.length, 'items');
        
        // Process content in batches for better performance with large datasets
        const batchSize = 50; // Adjust based on performance testing
        let scoredContent = [];
        let processedCount = 0;
        
        const processNextBatch = () => {
          if (processedCount >= content.length) {
            finishProcessing(scoredContent);
            return;
          }
          
          const startIndex = processedCount;
          const endIndex = Math.min(startIndex + batchSize, content.length);
          const batch = content.slice(startIndex, endIndex);
          
          // Score each item in this batch
          const batchResults = batch.map(item => {
            const scoreResult = scoreContentAgainstProfile(item, user);
            return { 
              ...item, 
              score: scoreResult.score, 
              passesThreshold: scoreResult.passesThreshold,
              matches: scoreResult.matches // Include match details for debugging
            };
          });
          
          scoredContent = [...scoredContent, ...batchResults];
          processedCount += batchSize;
          
          // Use setTimeout with 0 delay to avoid blocking the main thread
          setTimeout(processNextBatch, 0);
        };
        
        const finishProcessing = (results) => {
          // Debug first few scores
          if (results.length > 0) {
            console.log('Sample item scores:', 
              results.slice(0, 3).map(item => ({
                id: item.id,
                title: item.title,
                score: item.score,
                passes: item.passesThreshold,
                matches: Object.keys(item.matches.weightedSignals || {}).length > 0 
                  ? item.matches.weightedSignals 
                  : item.matches.preferredTags
              }))
            );
          }
          
          // Get threshold from user preferences or use default
          const threshold = user?.curationSignature?.scoreThreshold || 0.5;
          console.log('Using score threshold:', threshold);
          
          // Filter and sort by score
          const filtered = results
            .filter(item => item.passesThreshold)
            .sort((a, b) => b.score - a.score);
          
          console.log('After filtering, showing', filtered.length, 'items');
          
          // If no items pass the threshold, show some of the highest-scoring items anyway
          if (filtered.length === 0 && results.length > 0) {
            console.log('No items passed threshold, showing top items');
            const topItems = results
              .sort((a, b) => b.score - a.score)
              .slice(0, Math.min(10, results.length));
            setFilteredContent(topItems);
          } else {
            setFilteredContent(filtered);
          }
          
          setIsLoading(false);
        };
        
        // Start batch processing
        processNextBatch();
        
      } catch (error) {
        console.error('Error filtering content:', error);
        // Fallback to unfiltered content in case of error
        setFilteredContent(content);
        setIsLoading(false);
      }
    };

    applyFilter();
  }, [content, user, filterMode, savedItems]);

  // Call the callback function when filtered content changes
  useEffect(() => {
    if (onFilteredContent && !isLoading) {
      onFilteredContent(filteredContent);
    }
  }, [filteredContent, isLoading, onFilteredContent]);

  return null; // This is a logic component with no UI
};

// Define semantic relationships between terms for enhanced matching
const semanticRelationships = {
  // Design styles
  'minimalist': ['clean lines', 'simple', 'modern', 'scandinavian', 'japanese'],
  'brutalist': ['concrete', 'raw', 'industrial', 'monolithic'],
  'midcentury': ['mid-century', 'retro', '1950s', 'eames', 'modern'],
  'mid-century': ['midcentury', 'retro', '1950s', 'eames', 'modern'],
  'industrial': ['factory', 'warehouse', 'metal', 'steel', 'raw', 'urban'],
  'scandinavian': ['nordic', 'hygge', 'minimal', 'swedish', 'danish', 'light'],
  'japanese': ['zen', 'wabi-sabi', 'minimalist', 'shoji', 'tatami'],
  'modern': ['contemporary', 'current', 'sleek', 'minimalist'],
  'traditional': ['classic', 'historical', 'conventional', 'timeless'],
  
  // Materials
  'wood': ['oak', 'walnut', 'teak', 'pine', 'wooden', 'timber'],
  'concrete': ['cement', 'brutalist', 'raw', 'stone'],
  'glass': ['transparent', 'window', 'crystal', 'glazing'],
  'steel': ['metal', 'iron', 'industrial', 'stainless'],
  'marble': ['stone', 'granite', 'limestone', 'travertine'],
  'leather': ['hide', 'suede', 'upholstery'],
  
  // Colors
  'monochrome': ['black and white', 'grayscale', 'neutral'],
  'neutral': ['beige', 'cream', 'gray', 'white', 'earth tones'],
  'earth tones': ['brown', 'terracotta', 'rust', 'clay', 'neutral'],
  'bold': ['vibrant', 'colorful', 'saturated', 'bright'],
  
  // Spaces
  'residential': ['home', 'house', 'apartment', 'living space'],
  'commercial': ['office', 'retail', 'business', 'workspace'],
  'outdoor': ['garden', 'landscape', 'exterior', 'patio'],
};

/**
 * Scores content against a user's Digital Card profile
 * @param {Object} contentItem - Content item to score
 * @param {Object} userProfile - User profile with curation signature
 * @returns {Object} Score object with score, passesThreshold, and matches properties
 */
function scoreContentAgainstProfile(contentItem, userProfile) {
  // Default values if user profile is missing
  if (!userProfile || !userProfile.curationSignature) {
    return { score: 0.5, passesThreshold: true, matches: {} };
  }

  const {
    preferredTags = [],
    avoidedTags = [],
    materialBias = [],
    styleBias = [],
    influences = [],
    colorPalette = [],
    scoreThreshold = 0.5, // Lower default threshold to show more content
    signalWeights = {} // Use the signal weights if available
  } = userProfile.curationSignature;

  // Extract content signals, tags and text
  const itemSignals = Array.isArray(contentItem.signals) 
    ? contentItem.signals.map(signal => signal.toLowerCase()) 
    : [];
    
  const itemTags = Array.isArray(contentItem.tags) 
    ? contentItem.tags.map(tag => tag.toLowerCase()) 
    : [];
  
  const itemDescription = contentItem.description?.toLowerCase() || '';
  const itemTitle = contentItem.title?.toLowerCase() || '';
  const itemText = `${itemTitle} ${itemDescription}`;
  const itemSource = contentItem.source?.toLowerCase() || '';
  const itemCategory = contentItem.category?.toLowerCase() || '';

  // Combine all text elements for comprehensive matching
  const allItemText = [
    ...itemSignals,
    ...itemTags,
    itemText,
    itemSource,
    itemCategory
  ].join(' ');

  let score = 0;
  let maxPossibleScore = 0; // Maximum possible positive score
  let maxNegativeScore = 0; // Maximum possible negative score
  
  const matches = {
    preferredTags: [],
    avoidedTags: [],
    materials: [],
    styles: [],
    influences: [],
    weightedSignals: {}, // Store matched weighted signals
    semanticMatches: {}  // Store semantic matches
  };

  // Helper function to escape special regex characters
  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  
  // Helper function to calculate similarity between two strings (0-1)
  function stringSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    
    // Exact match
    if (s1 === s2) return 1;
    
    // Check if one is contained in the other
    if (s1.includes(s2) || s2.includes(s1)) {
      // Length ratio determines similarity
      const ratio = Math.min(s1.length, s2.length) / Math.max(s1.length, s2.length);
      return 0.7 + (ratio * 0.3); // Scale between 0.7-1.0 for containment
    }
    
    // Simple word distance for short terms
    if (s1.length < 10 && s2.length < 10) {
      // Count matching characters
      let matches = 0;
      const maxLength = Math.max(s1.length, s2.length);
      
      for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
        if (s1[i] === s2[i]) matches++;
      }
      
      return matches / maxLength;
    }
    
    // For longer strings, use a simpler approach to avoid performance issues
    // Check for common prefixes/suffixes and word overlap
    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);
    
    // Count common words
    const commonWords = words1.filter(w => words2.includes(w)).length;
    return commonWords / Math.max(words1.length, words2.length);
  }

  // PRIORITY 1: Apply explicit signal weights from user preferences
  if (Object.keys(signalWeights).length > 0) {
    Object.entries(signalWeights).forEach(([signal, weight]) => {
      const signalLower = signal.toLowerCase();
      const weightValue = parseInt(weight, 10);
      
      // Skip neutral weights
      if (weightValue === 0) return;
      
      // Track maximum possible scores
      if (weightValue > 0) {
        maxPossibleScore += weightValue;
      } else {
        maxNegativeScore += Math.abs(weightValue);
      }
      
      // Check for matches with improved word boundary detection
      let matchFound = false;
      let matchStrength = 0;
      
      // 1. Direct match in signals or tags (strongest match)
      if (itemSignals.includes(signalLower) || itemTags.includes(signalLower)) {
        matchStrength = 1.0; // Full strength
        matchFound = true;
      } 
      // 2. Word boundary match in text (medium match)
      else {
        // Create word boundary regex for more accurate matching
        const wordRegex = new RegExp(`\\b${escapeRegExp(signalLower)}\\b`, 'i');
        
        if (wordRegex.test(allItemText)) {
          matchStrength = 0.7; // Better than substring but not as good as tag match
          matchFound = true;
        }
        // 3. Partial match as fallback (weakest match)
        else if (allItemText.includes(signalLower)) {
          matchStrength = 0.3; // Reduced weight for substring matches
          matchFound = true;
        }
      }
      
      // If no direct match was found, check for similar tags (only for positive weights)
      if (!matchFound && weightValue > 0) {
        const similarityThreshold = 0.8; // Require high similarity
        
        for (const itemTag of itemTags) {
          const similarity = stringSimilarity(signalLower, itemTag);
          if (similarity >= similarityThreshold) {
            // Apply partial weight based on similarity
            matchStrength = similarity * 0.6; // Reduce weight for similar matches
            matchFound = true;
            break; // Stop after finding first good match
          }
        }
      }
      
      // Apply the score if a match was found
      if (matchFound) {
        const weightedScore = weightValue * matchStrength;
        score += weightedScore;
        matches.weightedSignals[signal] = weightedScore;
        
        // Record matches in appropriate categories
        if (weightValue > 0) {
          if (!matches.preferredTags.includes(signal)) {
            matches.preferredTags.push(signal);
          }
        } else if (weightValue < 0) {
          if (!matches.avoidedTags.includes(signal)) {
            matches.avoidedTags.push(signal);
          }
        }
      }
    });
    
    // PRIORITY 1.5: Apply semantic relationship boosts for weighted signals
    // Only apply semantic matches if we don't have strong direct matches already
    const directMatchStrength = Object.values(matches.weightedSignals).reduce((sum, val) => sum + val, 0);
    const hasManyDirectMatches = Object.keys(matches.weightedSignals).length > 3;
    
    if (!hasManyDirectMatches && directMatchStrength < maxPossibleScore * 0.5) {
      Object.entries(signalWeights).forEach(([signal, weight]) => {
        const weightValue = parseInt(weight, 10);
        if (weightValue <= 0) return; // Only boost for positive preferences
        
        // Check for semantic relationships
        const relatedTerms = semanticRelationships[signal.toLowerCase()] || [];
        for (const relatedTerm of relatedTerms) {
          // Skip if we already matched this term directly
          if (matches.weightedSignals[relatedTerm]) continue;
          
          const termLower = relatedTerm.toLowerCase();
          let semanticMatchFound = false;
          
          // Check if content matches any related terms
          if (itemTags.includes(termLower) || itemSignals.includes(termLower)) {
            // Strong semantic match
            semanticMatchFound = true;
            const semanticBoost = weightValue * 0.4; // 40% of original weight
            score += semanticBoost;
            matches.semanticMatches[`${signal}→${relatedTerm}`] = semanticBoost;
          } 
          // Check for word boundary match in text
          else {
            const wordRegex = new RegExp(`\\b${escapeRegExp(termLower)}\\b`, 'i');
            if (wordRegex.test(allItemText)) {
              semanticMatchFound = true;
              const semanticBoost = weightValue * 0.2; // 20% of original weight
              score += semanticBoost;
              matches.semanticMatches[`${signal}→${relatedTerm}`] = semanticBoost;
            }
          }
        }
      });
    }
  }
  
  // PRIORITY 2: Fall back to traditional preferences if no weighted signals
  if (Object.keys(signalWeights).length === 0) {
    // Match preferred tags (with more weight on exact signal/tag matches)
    preferredTags.forEach(tag => {
      if (tag && typeof tag === 'string') {
        const tagLower = tag.toLowerCase();
        
        // Extra score for signal match
        if (itemSignals.includes(tagLower)) {
          score += 3;
          maxPossibleScore += 3;
          matches.preferredTags.push(tag);
        }
        // Good score for tag match
        else if (itemTags.includes(tagLower)) {
          score += 2;
          maxPossibleScore += 2;
          matches.preferredTags.push(tag);
        }
        // Word boundary match in text (better than simple substring)
        else {
          const wordRegex = new RegExp(`\\b${escapeRegExp(tagLower)}\\b`, 'i');
          if (wordRegex.test(allItemText)) {
            score += 1.5;
            maxPossibleScore += 1.5;
            matches.preferredTags.push(tag);
          }
          // Lower score for text mention
          else if (allItemText.includes(tagLower)) {
            score += 0.7;
            maxPossibleScore += 0.7;
          }
        }
        
        // Check semantic relationships for this tag
        const relatedTerms = semanticRelationships[tagLower] || [];
        for (const relatedTerm of relatedTerms) {
          const termLower = relatedTerm.toLowerCase();
          if (itemSignals.includes(termLower) || itemTags.includes(termLower)) {
            score += 1; // Semantic match bonus
            maxPossibleScore += 1;
            matches.semanticMatches[`${tag}→${relatedTerm}`] = 1;
          }
        }
      }
    });

    // Penalize avoided tags (with stronger penalty for direct matches)
    avoidedTags.forEach(tag => {
      if (tag && typeof tag === 'string') {
        const tagLower = tag.toLowerCase();
        
        if (itemSignals.includes(tagLower) || itemTags.includes(tagLower)) {
          score -= 5; // Stronger penalty
          maxNegativeScore += 5;
          matches.avoidedTags.push(tag);
        }
        else {
          // Word boundary match
          const wordRegex = new RegExp(`\\b${escapeRegExp(tagLower)}\\b`, 'i');
          if (wordRegex.test(allItemText)) {
            score -= 3;
            maxNegativeScore += 3;
            matches.avoidedTags.push(tag);
          }
          // Substring match (weaker penalty)
          else if (allItemText.includes(tagLower)) {
            score -= 1.5;
            maxNegativeScore += 1.5;
          }
        }
      }
    });

    // Bonus for materials/styles/colors
    [...materialBias, ...styleBias, ...colorPalette].forEach(tag => {
      if (tag && typeof tag === 'string') {
        const tagLower = tag.toLowerCase();
        
        if (itemSignals.includes(tagLower) || itemTags.includes(tagLower)) {
          score += 2;
          maxPossibleScore += 2;
          
          if (materialBias.includes(tag)) matches.materials.push(tag);
          if (styleBias.includes(tag)) matches.styles.push(tag);
        }
        else {
          // Word boundary match
          const wordRegex = new RegExp(`\\b${escapeRegExp(tagLower)}\\b`, 'i');
          if (wordRegex.test(allItemText)) {
            score += 1.2;
            maxPossibleScore += 1.2;
            
            if (materialBias.includes(tag)) matches.materials.push(tag);
            if (styleBias.includes(tag)) matches.styles.push(tag);
          }
          // Substring match
          else if (allItemText.includes(tagLower)) {
            score += 0.8;
            maxPossibleScore += 0.8;
          }
        }
        
        // Check for semantic relationships
        const relatedTerms = semanticRelationships[tagLower] || [];
        for (const relatedTerm of relatedTerms) {
          const termLower = relatedTerm.toLowerCase();
          if (itemSignals.includes(termLower) || itemTags.includes(termLower)) {
            score += 0.8;
            maxPossibleScore += 0.8;
            matches.semanticMatches[`${tag}→${relatedTerm}`] = 0.8;
          }
        }
      }
    });

    // Influence match (with more weight)
    influences.forEach(name => {
      if (name && typeof name === 'string') {
        const nameLower = name.toLowerCase();
        
        if (itemSignals.includes(nameLower) || itemTags.includes(nameLower)) {
          score += 4;
          maxPossibleScore += 4;
          matches.influences.push(name);
        }
        else {
          // Word boundary match
          const wordRegex = new RegExp(`\\b${escapeRegExp(nameLower)}\\b`, 'i');
          if (wordRegex.test(allItemText)) {
            score += 3;
            maxPossibleScore += 3;
            matches.influences.push(name);
          }
          // Substring match
          else if (allItemText.includes(nameLower)) {
            score += 1.5;
            maxPossibleScore += 1.5;
          }
        }
      }
    });

    // Source preference (minor boost for preferred sources)
    const preferredSources = userProfile.curationSignature.preferredSources || [];
    preferredSources.forEach(source => {
      if (source && typeof source === 'string' && itemSource.includes(source.toLowerCase())) {
        score += 1;
        maxPossibleScore += 1;
      }
    });
  }

  // If we have no criteria to score against, ensure content is shown
  if (maxPossibleScore === 0 && maxNegativeScore === 0) {
    return {
      score: 0.75, // Default good enough score
      passesThreshold: true,
      matches
    };
  }

  // Enhanced score normalization
  let normalizedScore;

  // If we have both positive and negative weights
  if (maxPossibleScore > 0 && maxNegativeScore > 0) {
    // Normalize to range [0,1]
    normalizedScore = (score + maxNegativeScore) / (maxPossibleScore + maxNegativeScore);
  } 
  // If we only have positive weights
  else if (maxPossibleScore > 0) {
    // Normalize positive-only scores with a sigmoid-like curve
    // This gives more distinction between high and low matches
    normalizedScore = score / maxPossibleScore;
    // Apply sigmoid-like transform to spread scores more evenly
    normalizedScore = 1 / (1 + Math.exp(-6 * (normalizedScore - 0.5)));
  }
  // If we only have negative weights
  else if (maxNegativeScore > 0) {
    // For negative-only, we want to penalize matches more aggressively
    normalizedScore = 1 - (Math.min(maxNegativeScore, Math.abs(score)) / maxNegativeScore);
    // Apply power transform to make penalties more aggressive
    normalizedScore = Math.pow(normalizedScore, 1.5);
  } 
  // No weights at all
  else {
    normalizedScore = 0.5; // Fallback to neutral score
  }
  
  // Cap the score between 0 and 1
  const cappedScore = Math.max(0, Math.min(1, normalizedScore));

  return {
    score: cappedScore,
    passesThreshold: cappedScore >= scoreThreshold,
    matches
  };
}

export default DigitalCardFilter;